/**
 * Ligação das telas do financeiro com a viagem ativa.
 *
 * Existe para que as telas continuem recebendo o que precisam por prop — o que
 * as mantém testáveis sem contexto — enquanto o contexto resolve de onde vem.
 */
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ErrorState, Skeleton } from '../../ui/primitives';
import { useTrip } from '../../trip/TripContext';
import { readExpense } from './api';
import { DespesaScreen } from './DespesaScreen';
import { FecharScreen } from './FecharScreen';
import { FinanceiroScreen } from './FinanceiroScreen';
import { NovaDespesaScreen } from './NovaDespesaScreen';

function ComViagem({
  render,
}: {
  render: (t: ReturnType<typeof useTrip>) => ReactElement;
}) {
  const viagem = useTrip();

  if (viagem.carregando) {
    return (
      <section className="fin">
        <Skeleton height={48} count={3} />
      </section>
    );
  }

  if (viagem.erro) {
    return (
      <section className="fin">
        <ErrorState
          title="Não foi possível carregar a viagem"
          description="Verifique a conexão. O que já está no aparelho continua salvo."
          onRetry={() => window.location.reload()}
        />
      </section>
    );
  }

  return render(viagem);
}

export const FinanceiroRoute = () => (
  <ComViagem
    render={(v) => (
      <FinanceiroScreen tripId={v.tripId} currency={v.currency} meuUserId={v.meuUserId} />
    )}
  />
);

export const NovaDespesaRoute = () => {
  // "Nova despesa" no selo de salva navega para a mesma rota: a chave da
  // navegação remonta a tela e o formulário nasce limpo.
  const { key } = useLocation();
  return (
    <ComViagem
      render={(v) => (
        <NovaDespesaScreen
          key={key}
          tripId={v.tripId}
          currency={v.currency}
          meuMemberId={v.meuMemberId}
          membros={v.members.map((m) => ({
            memberId: m.memberId,
            nome: m.name,
            familyId: m.familyId,
            familyName: m.familyName,
            familyColor: m.familyColor,
          }))}
        />
      )}
    />
  );
};

/** Editar: a mesma tela de nova despesa, nascida com a despesa do servidor. */
export const EditarDespesaRoute = () => {
  const { despesa: expenseId } = useParams<{ despesa: string }>();
  return (
    <ComViagem
      render={(v) => <EditarDespesa tripId={v.tripId} expenseId={expenseId ?? ''} render={(existente) => (
        <NovaDespesaScreen
          tripId={v.tripId}
          currency={v.currency}
          meuMemberId={v.meuMemberId}
          existente={existente}
          membros={v.members.map((m) => ({
            memberId: m.memberId,
            nome: m.name,
            familyId: m.familyId,
            familyName: m.familyName,
            familyColor: m.familyColor,
          }))}
        />
      )} />}
    />
  );
};

function EditarDespesa({ tripId, expenseId, render }: { tripId: string; expenseId: string; render: (d: Awaited<ReturnType<typeof readExpense>>) => ReactElement }) {
  const dados = useQuery({ queryKey: ['financeiro', 'despesa', tripId, expenseId], queryFn: () => readExpense(tripId, expenseId), enabled: Boolean(expenseId) });
  if (dados.isLoading) {
    return (
      <section className="fin">
        <Skeleton height={48} count={3} />
      </section>
    );
  }
  if (dados.isError || !dados.data) {
    return (
      <section className="fin">
        <ErrorState title="Despesa não encontrada" description="Ela pode ter sido excluída." onRetry={() => window.history.back()} />
      </section>
    );
  }
  return render(dados.data);
}

export const FecharRoute = () => (
  <ComViagem
    render={(v) => <FecharScreen tripId={v.tripId} currency={v.currency} nomes={v.nomes} />}
  />
);

export const DespesaRoute = () => (
  <ComViagem
    render={(v) => (
      <DespesaScreen
        tripId={v.tripId}
        nomes={v.nomes}
        meuMemberId={v.meuMemberId}
        meuUserId={v.meuUserId}
        podeEditarDeOutros={v.permissions.includes('edit_others_expenses')}
      />
    )}
  />
);
